<template>
    <transition name="video-upload-progress">
        <aside v-if="activeJob" class="floating-video-upload" role="status" aria-live="polite">
            <section v-if="isExpanded" class="floating-video-upload__panel">
                <header class="floating-video-upload__panel-header">
                    <h3>Uploads</h3>

                    <div class="floating-video-upload__panel-actions">
                        <button
                            v-if="canDismiss"
                            class="floating-video-upload__dismiss-text-button"
                            type="button"
                            aria-label="Dismiss upload status"
                            title="Dismiss"
                            @click="dismissJob(activeJob.id)"
                        >
                            Dismiss
                        </button>
                    </div>
                </header>

                <div class="floating-video-upload__destination">
                    {{ destinationLabel }}
                    <router-link v-if="eventLink" :to="eventLink">
                        {{ activeJob.eventTitle }}
                    </router-link>
                    <span v-else>{{ activeJob.eventTitle }}</span>
                </div>

                <div class="floating-video-upload__file-row">
                    <div class="floating-video-upload__file-icon" :class="statusClass">
                        <font-awesome-icon :icon="statusIcon" />
                    </div>

                    <div class="floating-video-upload__file-content">
                        <div class="floating-video-upload__file-heading">
                            <strong>{{ currentFileName }}</strong>
                            <v-btn
                                v-if="canCancelActiveJob"
                                size="x-small"
                                color="#FF6C37"
                                variant="text"
                                @click="cancelActiveUpload"
                            >
                                Cancel
                            </v-btn>
                        </div>

                        <div class="floating-video-upload__file-meta">
                            <span v-if="fileExtension" class="floating-video-upload__extension">{{
                                fileExtension
                            }}</span>
                            <span>{{ detailedProgressLabel }}</span>
                        </div>

                        <v-progress-linear
                            v-if="showPercent"
                            :model-value="activeJobPercent"
                            bg-color="#E5E7EB"
                            :color="progressColor"
                            height="6"
                        />
                        <v-progress-linear v-else indeterminate bg-color="#E5E7EB" :color="progressColor" height="6" />
                    </div>
                </div>

                <div v-if="activeJob.files.length > 0" class="floating-video-upload__file-list">
                    <div class="floating-video-upload__file-list-header">
                        <span>Videos</span>
                        <span>{{ fileProgressLabel }}</span>
                    </div>

                    <div
                        v-for="file in activeJob.files"
                        :key="file.id"
                        class="floating-video-upload__file-item"
                        :class="{ 'floating-video-upload__file-item--active': file.status === 'uploading' }"
                    >
                        <span class="floating-video-upload__file-status" :class="fileStatusClass(file.status)">
                            <font-awesome-icon :icon="fileStatusIcon(file.status)" />
                        </span>

                        <div class="floating-video-upload__file-item-copy">
                            <strong>{{ file.index + 1 }}. {{ file.name }}</strong>
                            <small>{{ fileStatusLabel(file) }}</small>
                        </div>

                        <span class="floating-video-upload__file-item-percent">
                            {{ filePercentLabel(file) }}
                        </span>
                    </div>
                </div>

                <p v-if="activeJob.status === 'error'" class="floating-video-upload__error">
                    {{ activeJob.errorMessage || 'Video upload failed.' }}
                </p>

                <footer class="floating-video-upload__panel-footer">
                    <p class="floating-video-upload__footer-note">
                        {{ footerNote }}
                    </p>

                    <div class="floating-video-upload__footer-actions">
                        <v-btn v-if="canViewService" size="small" variant="outlined" @click="viewService">
                            View Service
                        </v-btn>
                        <button
                            class="floating-video-upload__icon-button"
                            type="button"
                            aria-label="Collapse upload details"
                            title="Collapse"
                            @click="isExpanded = false"
                        >
                            <font-awesome-icon icon="fa-solid fa-chevron-down" />
                        </button>
                    </div>
                </footer>
            </section>

            <button v-else class="floating-video-upload__dock" type="button" @click="isExpanded = true">
                <span class="floating-video-upload__dock-icon" :class="statusClass">
                    <font-awesome-icon :icon="statusIcon" />
                </span>

                <span class="floating-video-upload__dock-copy">
                    <strong>{{ compactTitle }}</strong>
                    <small>{{ compactSubtitle }}</small>
                </span>

                <span class="floating-video-upload__dock-chevron">
                    <font-awesome-icon icon="fa-solid fa-chevron-up" />
                </span>
            </button>
        </aside>
    </transition>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { useServiceVideoUploadJob } from '@/composables/video-upload/useServiceVideoUploadJob';
import { useVideoUploadQueue, type VideoUploadJobFile, type VideoUploadJobFileStatus } from '@/stores/videoUploadQueue';

const router = useRouter();
const uploadQueue = useVideoUploadQueue();
const { activeJob, activeJobPercent, canCancelActiveJob, isTerminal } = storeToRefs(uploadQueue);
const { dismissJob } = uploadQueue;
const { cancelServiceVideoUpload } = useServiceVideoUploadJob();
const isExpanded = ref(false);
const completeAutoDismissDelayMs = 120_000;
let completeAutoDismissTimeout: ReturnType<typeof setTimeout> | null = null;

function clearCompleteAutoDismissTimeout(): void {
    if (!completeAutoDismissTimeout) {
        return;
    }

    clearTimeout(completeAutoDismissTimeout);
    completeAutoDismissTimeout = null;
}

watch(
    () => ({ id: activeJob.value?.id, status: activeJob.value?.status }),
    jobState => {
        clearCompleteAutoDismissTimeout();

        const jobId = jobState.id;

        if (!jobId || jobState.status !== 'complete') {
            return;
        }

        completeAutoDismissTimeout = setTimeout(() => {
            dismissJob(jobId);
            completeAutoDismissTimeout = null;
        }, completeAutoDismissDelayMs);
    },
);

onUnmounted(() => {
    clearCompleteAutoDismissTimeout();
});

const compactTitle = computed(() => {
    const job = activeJob.value;

    if (!job) {
        return 'Video upload';
    }

    switch (job.status) {
        case 'queued':
            return 'Upload queued';
        case 'uploading':
            return `Uploading ${itemCountLabel.value}`;
        case 'processing':
            return 'Preparing render';
        case 'complete':
            return 'Upload complete';
        case 'error':
            return 'Upload failed';
        case 'cancelled':
            return 'Upload cancelled';
        default:
            return 'Video upload';
    }
});

const compactSubtitle = computed(() => {
    const job = activeJob.value;

    if (!job) {
        return '';
    }

    if (job.status === 'uploading' && showPercent.value) {
        return `${activeJobPercent.value}% complete`;
    }

    if (job.status === 'error') {
        return job.errorMessage || 'Action needed';
    }

    if (job.status === 'complete') {
        return 'Render job started';
    }

    if (job.status === 'cancelled') {
        return 'Dismiss when ready';
    }

    return fileProgressLabel.value;
});

const footerNote = computed(() => {
    switch (activeJob.value?.status) {
        case 'complete':
            return 'Your upload is complete and the render job has started.';
        case 'error':
            return 'The upload stopped before the render could be created.';
        case 'cancelled':
            return 'The upload was cancelled and can be dismissed.';
        default:
            return 'You can keep working while this continues in the background.';
    }
});

const destinationLabel = computed(() => {
    switch (activeJob.value?.status) {
        case 'complete':
            return 'Uploaded to';
        case 'error':
            return 'Upload failed for';
        case 'cancelled':
            return 'Upload cancelled for';
        default:
            return 'Uploading to';
    }
});

const currentFileName = computed(() => activeJob.value?.currentFileName || 'Service video');

const fileExtension = computed(() => {
    const extension = currentFileName.value.split('.').pop();

    if (!extension || extension === currentFileName.value) {
        return null;
    }

    return extension.toUpperCase();
});

const fileProgressLabel = computed(() => {
    const job = activeJob.value;

    if (!job || job.totalFiles <= 0) {
        return 'No videos selected';
    }

    const currentFile = Math.min(job.currentFileIndex, job.totalFiles);

    return `Video ${currentFile} of ${job.totalFiles}`;
});

const detailedProgressLabel = computed(() => {
    const job = activeJob.value;

    if (!job) {
        return '';
    }

    if (job.status === 'processing') {
        return 'Creating render job';
    }

    if (job.status === 'complete') {
        return 'Render job started';
    }

    if (job.status === 'cancelled') {
        return 'Cancelled';
    }

    if (job.status === 'error') {
        return 'Failed';
    }

    if (!showPercent.value) {
        return fileProgressLabel.value;
    }

    return `${formatBytes(job.uploadedBytes)} / ${formatBytes(job.totalBytes)} - ${activeJobPercent.value}% complete`;
});

const itemCountLabel = computed(() => {
    const totalFiles = activeJob.value?.totalFiles ?? 0;

    return `${totalFiles || 1} ${totalFiles === 1 ? 'item' : 'items'}`;
});

const showPercent = computed(() => {
    const job = activeJob.value;

    if (!job) {
        return false;
    }

    return job.totalBytes > 0 || job.status === 'complete';
});

const canDismiss = computed(() => Boolean(activeJob.value && isTerminal.value));
const eventLink = computed<RouteLocationRaw | null>(() => {
    const job = activeJob.value;

    if (!job?.serviceSlug || !job.eventExternalId) {
        return null;
    }

    return {
        name: 'EditServices',
        params: {
            slug: job.serviceSlug,
            event: job.eventExternalId,
        },
    };
});

const canViewService = computed(() => {
    const job = activeJob.value;

    return Boolean(job?.status === 'complete' && eventLink.value);
});

const statusClass = computed(() => `is-${activeJob.value?.status ?? 'queued'}`);
const progressColor = computed(() => {
    switch (activeJob.value?.status) {
        case 'complete':
            return '#2E7D32';
        case 'error':
            return '#C62828';
        case 'cancelled':
            return '#737373';
        case 'processing':
            return '#475569';
        default:
            return '#FF6C37';
    }
});

const statusIcon = computed(() => {
    switch (activeJob.value?.status) {
        case 'complete':
            return 'fa-solid fa-check';
        case 'error':
            return 'fa-solid fa-triangle-exclamation';
        case 'cancelled':
            return 'fa-solid fa-ban';
        case 'processing':
            return 'fa-solid fa-gear';
        default:
            return 'fa-solid fa-arrow-up';
    }
});

function cancelActiveUpload(): void {
    const job = activeJob.value;

    if (!job) {
        return;
    }

    cancelServiceVideoUpload(job.id);
}

async function viewService(): Promise<void> {
    const link = eventLink.value;

    if (!link) {
        return;
    }

    await router.push(link);
}

function formatBytes(bytes: number): string {
    if (bytes <= 0) {
        return '0 MB';
    }

    const megabytes = bytes / 1048576;

    if (megabytes < 1024) {
        return `${Math.round(megabytes)} MB`;
    }

    return `${(megabytes / 1024).toFixed(1)} GB`;
}

function fileStatusIcon(status: VideoUploadJobFileStatus): string {
    switch (status) {
        case 'uploaded':
        case 'imported':
        case 'complete':
            return 'fa-solid fa-check';
        case 'processing':
            return 'fa-solid fa-gear';
        case 'error':
            return 'fa-solid fa-triangle-exclamation';
        case 'cancelled':
            return 'fa-solid fa-ban';
        case 'uploading':
            return 'fa-solid fa-arrow-up';
        default:
            return 'fa-solid fa-clock';
    }
}

function fileStatusClass(status: VideoUploadJobFileStatus): string {
    return `is-${status}`;
}

function fileStatusLabel(file: VideoUploadJobFile): string {
    switch (file.status) {
        case 'uploaded':
            return 'Uploaded';
        case 'imported':
            return 'Imported video';
        case 'complete':
            return 'Ready for render';
        case 'processing':
            return 'Preparing render';
        case 'error':
            return 'Failed';
        case 'cancelled':
            return 'Cancelled';
        case 'uploading':
            return file.size > 0 ? `${formatBytes(file.uploadedBytes)} / ${formatBytes(file.size)}` : 'Uploading';
        default:
            return 'Queued';
    }
}

function filePercentLabel(file: VideoUploadJobFile): string {
    if (file.isImported) {
        return 'Ready';
    }

    if (file.status === 'queued') {
        return 'Queued';
    }

    if (file.status === 'error' || file.status === 'cancelled') {
        return fileStatusLabel(file);
    }

    if (file.status === 'uploaded' || file.status === 'complete' || file.status === 'processing') {
        return '100%';
    }

    if (file.size <= 0) {
        return '';
    }

    return `${Math.min(100, Math.round((file.uploadedBytes / file.size) * 100))}%`;
}
</script>

<style scoped lang="scss">
.floating-video-upload {
    position: fixed;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    z-index: 1000;
    color: #241914;
}

.floating-video-upload__dock,
.floating-video-upload__panel {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.14);
}

.floating-video-upload__dock {
    position: relative;
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    align-items: center;
    width: min(442px, calc(100vw - 32px));
    min-height: 74px;
    padding: 8px 40px 8px 8px;
    border-radius: 16px;
    color: inherit;
    text-align: left;
    cursor: pointer;
}

.floating-video-upload__dock-icon,
.floating-video-upload__file-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ff6c37;
    color: white;
}

.floating-video-upload__dock-icon {
    width: 56px;
    height: 56px;
    border-radius: 8px;
    font-size: 1.4rem;
}

.floating-video-upload__dock-copy {
    display: grid;
    gap: 2px;
    min-width: 0;
    padding: 0 12px;
}

.floating-video-upload__dock-copy strong,
.floating-video-upload__file-heading strong {
    overflow: hidden;
    font-size: 0.95rem;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.floating-video-upload__dock-copy small {
    overflow: hidden;
    color: #6b7280;
    font-size: 0.8rem;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.floating-video-upload__dock-chevron {
    position: absolute;
    right: 14px;
    bottom: 12px;
    color: #6b7280;
    font-size: 0.9rem;
}

.floating-video-upload__panel {
    width: min(440px, calc(100vw - 32px));
    min-height: 300px;
    padding: 18px;
    border-radius: 18px;
}

.floating-video-upload__panel-header,
.floating-video-upload__file-heading,
.floating-video-upload__panel-actions,
.floating-video-upload__footer-actions {
    display: flex;
    align-items: center;
}

.floating-video-upload__panel-header,
.floating-video-upload__file-heading {
    justify-content: space-between;
}

.floating-video-upload__panel-header h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
}

.floating-video-upload__panel-actions,
.floating-video-upload__footer-actions {
    gap: 8px;
}

.floating-video-upload__icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    border: 0;
    background: #fff1eb;
    color: #9f3d17;
    cursor: pointer;
}

.floating-video-upload__dismiss-text-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 28px;
    padding: 0 12px;
    border: 1px solid rgba(0, 0, 0, 0.87);
    border-radius: 4px;
    background: #ffffff;
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.0892857143em;
    line-height: 1;
    text-transform: uppercase;
    cursor: pointer;
    transition:
        background-color 150ms ease,
        border-color 150ms ease,
        color 150ms ease;
}

.floating-video-upload__dismiss-text-button:hover {
    border-color: #ffbea5;
    background: #fff3ed;
    color: #9f3d17;
}

.floating-video-upload__destination {
    margin-top: 24px;
    color: #4b5563;
    font-size: 0.88rem;
    font-weight: 700;
}

.floating-video-upload__destination a,
.floating-video-upload__destination span {
    color: inherit;
    margin-left: 4px;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.floating-video-upload__file-row {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 12px;
    margin-top: 24px;
    padding: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #fafafa;
}

.floating-video-upload__file-icon {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    font-size: 0.78rem;
}

.floating-video-upload__file-content {
    display: grid;
    gap: 8px;
    min-width: 0;
}

.floating-video-upload__file-heading strong {
    color: #241914;
    font-size: 0.9rem;
}

.floating-video-upload__file-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    font-size: 0.75rem;
}

.floating-video-upload__extension {
    padding: 1px 5px;
    border-radius: 3px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 0.68rem;
    font-weight: 700;
}

.floating-video-upload__file-list {
    display: grid;
    gap: 8px;
    margin-top: 14px;
}

.floating-video-upload__file-list-header,
.floating-video-upload__file-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
}

.floating-video-upload__file-list-header {
    color: #6b7280;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
}

.floating-video-upload__file-item {
    grid-template-columns: 24px minmax(0, 1fr) auto;
    gap: 10px;
    padding: 9px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #ffffff;
}

.floating-video-upload__file-item--active {
    border-color: #ffbea5;
    background: #fff8f5;
}

.floating-video-upload__file-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: #e5e7eb;
    color: #4b5563;
    font-size: 0.68rem;
}

.floating-video-upload__file-item-copy {
    display: grid;
    gap: 2px;
    min-width: 0;

    strong,
    small {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        color: #241914;
        font-size: 0.82rem;
        line-height: 1.15;
    }

    small {
        color: #6b7280;
        font-size: 0.72rem;
        line-height: 1.15;
    }
}

.floating-video-upload__file-item-percent {
    color: #6b7280;
    font-size: 0.72rem;
    font-weight: 700;
}

.floating-video-upload__error {
    margin: 12px 0 0 40px;
    color: #b3261e;
    font-size: 0.82rem;
}

.floating-video-upload__panel-footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 16px;
    margin-top: 28px;
}

.floating-video-upload__footer-note {
    min-width: 0;
    max-width: none;
    margin: 0;
    color: #6b7280;
    font-size: 0.78rem;
    line-height: 1.35;
}

.floating-video-upload__footer-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
    align-self: end;
    min-width: 0;
    margin-left: auto;
}

.floating-video-upload__dock-copy,
.floating-video-upload__file-content {
    min-width: 0;
}

.is-complete {
    background: #21a67a;
}

.is-uploaded,
.is-imported {
    background: #21a67a;
    color: white;
}

.is-uploading {
    background: #ff6c37;
    color: white;
}

.is-queued {
    background: #e5e7eb;
    color: #4b5563;
}

.is-error {
    background: #d64545;
    color: white;
}

.is-cancelled {
    background: #6d6d6d;
    color: white;
}

.is-processing {
    background: #6f4fd8;
    color: white;
}

.video-upload-progress-enter-active,
.video-upload-progress-leave-active {
    transition: opacity 180ms ease;
}

.video-upload-progress-enter-from,
.video-upload-progress-leave-to {
    opacity: 0;
}

@media (max-width: 720px) {
    .floating-video-upload {
        right: 12px;
        bottom: 12px;
        left: 12px;
        transform: none;
    }

    .floating-video-upload__dock,
    .floating-video-upload__panel {
        width: 100%;
    }

    .floating-video-upload__panel {
        min-height: 360px;
    }

    .floating-video-upload__panel-footer {
        grid-template-columns: 1fr;
        margin-top: 24px;
    }

    .floating-video-upload__footer-note {
        max-width: none;
    }

    .floating-video-upload__footer-actions {
        justify-content: flex-start;
        margin-left: 0;
    }
}
</style>
